<template>
    <q-modal v-model="uploadModal.open" class="sl-modal" :content-css="{minWidth: '30vw' }">
      <q-modal-layout>
        <q-toolbar slot="header">
          <q-icon name="cloud upload"></q-icon>
          <q-toolbar-title>
            Upload Document
          </q-toolbar-title>
          <q-icon @click.native="onClose" name="clear" />
        </q-toolbar>

        <q-toolbar slot="footer">
          <q-toolbar-title>
          </q-toolbar-title>
          <q-btn label="Upload" color="green" @click="upload()"/>&nbsp;
          <q-btn outline color="faded" @click.native="onClose" label="Cancel" />          
        </q-toolbar>

        <div id="upload-body" class="layout-padding">
          <div>
            <h6> Details </h6>
            <q-field class="q-pa-sm"
            :error="$v.uploadForm.DOCUMENT_NAME.$error"
            error-label="Required">
              <q-input
                @blur="$v.uploadForm.DOCUMENT_NAME.$touch"
                float-label="Document Name"
                :value="uploadForm.DOCUMENT_NAME"
                @keyup.stop="updateUploadForm({ field:'DOCUMENT_NAME', value: $event.target.value })">
              </q-input>
            </q-field>
            <q-field class="q-pa-sm" v-if="!hideEntity" >
              <q-select 
                clearable
                :hide="hideEntity"
                float-label="Entity"
                :value="uploadForm.LK_ENTITY_ID"
                :options="entityOptions"
                @change="val => updateUploadForm({ field: 'LK_ENTITY_ID', value: val })"
              />
            </q-field>
            <q-field class="q-pa-sm" v-if="!hideEntity">
              <user-auto-complete 
                v-if="uploadForm.LK_ENTITY_ID === 1"
                :hide="hideEntity"
                :onSelect="selectUser"
                :userId="uploadForm.ENTITY_ITEM_ID"
              />
              <student-auto-complete 
                v-if="uploadForm.LK_ENTITY_ID === 2"
                :hide="hideEntity"
                :onSelect="selectStudent"
                :studentId="uploadForm.ENTITY_ITEM_ID"
              />
              <course-auto-complete 
                v-if="uploadForm.LK_ENTITY_ID === 3"
                :hide="hideEntity"
                :onSelect="selectCourse"
                :courseId="uploadForm.ENTITY_ITEM_ID"
              />
              <class-auto-complete 
                v-if="uploadForm.LK_ENTITY_ID === 4"
                :hide="hideEntity"
                :onSelect="selectClass"
                :classId="uploadForm.ENTITY_ITEM_ID"
              />
            </q-field>
            <q-field class="q-pa-sm"
            :error="$v.uploadForm.LK_DOCUMENT_TYPE_ID.$error"
            error-label="Required">
              <q-select 
                float-label="Document Type" 
                :value="uploadForm.LK_DOCUMENT_TYPE_ID"
                :options="documentTypeOptionsStripped"
                @change="val => updateUploadForm({ field: 'LK_DOCUMENT_TYPE_ID', value: val })"
              />
            </q-field>
          </div>
          <div class="q-pt-xl q-px-sm">
            <h6> Select Document </h6>
            <q-field
              :helper="fileTypeInfo"
              class="q-pa-sm"
              >
              <q-uploader 
                auto-expand
                ref="documentUpload"
                hide-upload-button
                url=""
                method="PUT"
                :url-factory="generateSignedUrl"
                :extensions="fileExtensions"
                :send-raw="true"
                @uploaded="onUploaded"
                @fail="onFail"
              />
            </q-field>
          </div>
        </div>
      </q-modal-layout>
      <info-modal
        :isOpen="showInfoModal"
        :closeModal="closeInfoModal"
        title="Thanks for uploading"
        body="Thanks for uploading your roster! No need to email your roster to the team. We've got it!"
      />
    </q-modal>
</template>

<script>
import { mapActions, mapGetters, mapState, mapMutations } from 'vuex'
import UserAutoComplete from '../User/UserAutoComplete'
import StudentAutoComplete from '../Student/StudentAutoComplete'
import CourseAutoComplete from '../Course/CourseAutoComplete'
import ClassAutoComplete from '../Class/ClassAutoComplete'
import { required } from 'vuelidate/lib/validators'
import InfoModal from '../Global/InfoModal.vue'

export default {
  name: 'UploadDialog',
  components: {
    UserAutoComplete,
    StudentAutoComplete,
    CourseAutoComplete,
    ClassAutoComplete,
    InfoModal,
  },
  props: {
    hideEntity: {
      Type: Boolean,
      default: false
    },
    entityItemId: {
      default: null
    }
  },
  async mounted () {
    await this.$store.dispatch('loadLookups')
    if(this.documentTypeOptions.length === 1) {
      this.updateUploadForm({ field: 'LK_DOCUMENT_TYPE_ID', value: this.documentTypeOptions[0].value })
    }
  },
  data () {
    return {
      module: this.$route.meta.module,
      headers: { 'Content-Type': 'text/rtf' },
      showInfoModal: false,
    }
  },
  validations() {
    return {
      uploadForm: {
        DOCUMENT_NAME: { required },
        LK_DOCUMENT_TYPE_ID: { required }
      }
    }
  },
  computed: {
    ...mapState('documents', {
      uploadModal: (state) => state.uploadModal,
      uploadForm: (state) => state.uploadForm
    }),
    ...mapGetters([
      'documentTypeOptions',
      'fileExtensions',
      'entityOptions'
    ]),
    fileTypeInfo () {
      return `Only accepts ${this.fileExtensions} extensions`
    },
    documentTypeOptionsStripped () {
      const vm = this
      if (vm.module === 'documents') return this.documentTypeOptions.filter(x =>  x.value === 25)
      return this.documentTypeOptions
    }
  },
  methods: {
    ...mapMutations('documents', [
      'updateUploadForm',
      'closeUpload',
      'clearDocumentForm',
      'clearUploadFormExceptEntityID'
    ]),
    ...mapActions('documents', [
      'generateSignedUrl',
      'refreshGrid',
      'fetch',
      'remove'
    ]),
    selectUser (selected) {
      this.updateUploadForm({ field: 'ENTITY_ITEM_ID', value: selected.item.USER_ID })
    },
    selectStudent (selected) {
      this.updateUploadForm({ field: 'ENTITY_ITEM_ID', value: selected.item.STUDENT_ID })
    },
    selectCourse (selected) {
      this.updateUploadForm({ field: 'ENTITY_ITEM_ID', value: selected.item.COURSE_ID })
    },
    selectClass (selected) {
      this.updateUploadForm({ field: 'ENTITY_ITEM_ID', value: selected.item.CLASS_ID })
    },
    upload () {
      this.$v.uploadForm.$touch()
      if (!this.$v.uploadForm.$invalid) {
        if(this.entityItemId) this.updateUploadForm({ field: 'ENTITY_ITEM_ID', value: this.entityItemId })
        this.$refs.documentUpload.upload()
      }
    },
    onClose () {
      this.closeUpload()
    },
    async onFail (e) {
      console.log(e)
      await this.remove(this.uploadForm.DOCUMENT_ID)
      this.$q.notify({
          message: 'There was an error uploading the document. Please try again or contact your administrator.',
          type: 'error'
        })
    },
    async onUploaded () {
      const vm = this
      const customFilter = []
      if (vm.module === 'users' || vm.module === 'instructorApprovals') { 
        customFilter.push({ field: 'LK_ENTITY_ID', filter: '1' })
      } else if (vm.module === 'students') {
        customFilter.push({ field: 'LK_ENTITY_ID', filter: '2' })
      } else if (vm.module === 'courses') {
        customFilter.push({ field: 'LK_ENTITY_ID', filter: '3' })
      } else if (vm.module === 'classes') {
        customFilter.push({ field: 'LK_ENTITY_ID', filter: '4' })
      }

      if (vm.module != 'documents') {
        customFilter.push({ field: 'ENTITY_ITEM_ID', filter: this.entityItemId })
      }

      // await vm.fetch({ customFilter: customFilter })
      setTimeout(function () {
        vm.$root.$emit("refreshGrid:documentGrid")
      }, 0)

      if (vm.uploadForm.LK_DOCUMENT_TYPE_ID == 1) { // roster
        vm.showInfoModal = true
      } else {
        vm.closeUpload()
      }

      vm.$q.notify({
        message: 'Uploaded files',
        type: 'positive'
      })
    },
    closeInfoModal() {
      this.showInfoModal = false
      this.closeUpload()
    }
  }
}
</script>

<style>
#upload-body {
  overflow-y: auto
}
</style>
